
import { useQuery } from "@tanstack/react-query";
import { dataCache } from '@/lib/dataCache';
import { checkSupabaseHealth } from '@/lib/supabaseClient';

interface ResilientDataOptions<T> {
  queryKey: string[];
  queryFn: () => Promise<T>;
  cacheKey: string;
  fallbackData?: T;
  staleTime?: number;
  gcTime?: number;
  retryAttempts?: number;
}

export const useResilientData = <T>({
  queryKey,
  queryFn,
  cacheKey,
  fallbackData,
  staleTime = 1000 * 60 * 5, // 5 minutes
  gcTime = 1000 * 60 * 10, // 10 minutes
  retryAttempts = 1 // Reduced retries for faster fallback
}: ResilientDataOptions<T>) => {
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      try {
        console.log(`[ResilientData] Fetching data for: ${cacheKey}`);
        
        // Try to fetch fresh data first - don't block on health check
        try {
          const data = await queryFn();
          
          // Cache successful response
          dataCache.set(cacheKey, data);
          console.log(`[ResilientData] Successfully fetched and cached: ${cacheKey}`);
          
          return data;
        } catch (fetchError) {
          console.log(`[ResilientData] Fetch failed for ${cacheKey}, checking alternatives`);
          
          // Try cached data first
          const cachedData = dataCache.get<T>(cacheKey);
          if (cachedData) {
            console.log(`[ResilientData] Using cached data for: ${cacheKey}`);
            return cachedData;
          }
          
          // Use fallback data if available
          if (fallbackData) {
            console.log(`[ResilientData] Using fallback data for: ${cacheKey}`);
            return fallbackData;
          }
          
          throw fetchError;
        }
      } catch (error) {
        console.error(`[ResilientData] All options exhausted for ${cacheKey}:`, error);
        throw error;
      }
    },
    retry: (failureCount, error) => {
      console.log(`[ResilientData] Retry attempt ${failureCount} for: ${cacheKey}`);
      
      // Don't retry if we have cached data or fallback
      const cachedData = dataCache.get<T>(cacheKey);
      if (cachedData || fallbackData) {
        return false;
      }
      
      return failureCount < retryAttempts;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000),
    staleTime,
    gcTime,
    networkMode: 'online' // Changed from 'offlineFirst' to be less restrictive
  });
};
